import { Liabilities, OptimizationTypes } from '@/enums/portfolio-optimization.enum'
import {
  CashFlowFileRecord,
  IBenchmark,
  IPreAfterTaxFileRecord,
} from '@/interfaces/portfolioOptimization.interface'

export interface RateFileRecord {
  years: number
  rate: number
}

export interface PreAfterTaxFileRecord {
  nonMunicipalTax: number
  municipalTax: {
    grandfathered: number
    nonGrandfathered: number
    zeros: number
  }
}

// Define the shape of the state
export type OptimizationParameterState = {
  isExpanded: boolean
  isComplete: boolean
  optimizationType: string
  methodType: string
  objectiveType: string
  objectiveItem: string
  riskModel: string
  benchmark?: IBenchmark
  liabilities: string[]
  cashFlowFileRecords: CashFlowFileRecord[]
  rateFileRecords: RateFileRecord[]
  borrowingRateFileRecords: RateFileRecord[]
  preAfterTaxFileRecords: Object
  contribution: string
  amount: number
}

// Define the action types as an enum
export enum OptimizationParameterActionType {
  SET_OPTIMIZATION_TYPE = 'SET_OPTIMIZATION_TYPE',
  SET_METHOD_TYPE = 'SET_METHOD_TYPE',
  SET_OBJECTIVE_TYPE = 'SET_OBJECTIVE_TYPE',
  SET_OBJECTIVE_ITEM = 'SET_OBJECTIVE_ITEM',
  SET_RISK_MODEL = 'SET_RISK_MODEL',
  SET_BENCHMARK = 'SET_BENCHMARK',
  SET_LIABILITIES = 'SET_LIABILITIES',
  SET_CASHFLOW_FILE_RECORDS = 'SET_CASHFLOW_FILE_RECORDS',
  SET_REINVESTMENT_RATE_FILE_RECORDS = 'SET_REINVESTMENT_RATE_FILE_RECORDS',
  SET_BORROWING_RATE_FILE_RECORDS = 'SET_BORROWING_RATE_FILE_RECORDS',
  SET_PRE_AFTER_TAX_FILE_RECORDS = 'SET_PRE_AFTER_TAX_FILE_RECORDS',
  SET_CONTRIBUTION = 'SET_CONTRIBUTION',
  SET_CONTRIBUTION_AMOUNT = 'SET_CONTRIBUTION_AMOUNT',
  TOGGLE_EXPANDED = 'TOGGLE_EXPANDED',
  SET_COMPLETE = 'SET_COMPLETE',
  RESET = 'RESET',
}

// Define the possible actions using the enum
export type Action =
  | {
      type: OptimizationParameterActionType.SET_OPTIMIZATION_TYPE
      payload: string
    }
  | { type: OptimizationParameterActionType.SET_METHOD_TYPE; payload: string }
  | {
      type: OptimizationParameterActionType.SET_OBJECTIVE_TYPE
      payload: string
    }
  | {
      type: OptimizationParameterActionType.SET_OBJECTIVE_ITEM
      payload: string
    }
  | { type: OptimizationParameterActionType.SET_RISK_MODEL; payload: string }
  | { type: OptimizationParameterActionType.SET_BENCHMARK; payload: IBenchmark }
  | { type: OptimizationParameterActionType.SET_LIABILITIES; payload: string[] }
  | {
      type: OptimizationParameterActionType.SET_CASHFLOW_FILE_RECORDS
      payload: CashFlowFileRecord[]
    }
  | {
      type: OptimizationParameterActionType.SET_REINVESTMENT_RATE_FILE_RECORDS
      payload: RateFileRecord[]
    }
  | {
      type: OptimizationParameterActionType.SET_BORROWING_RATE_FILE_RECORDS
      payload: RateFileRecord[]
    }
  | {
      type: OptimizationParameterActionType.SET_PRE_AFTER_TAX_FILE_RECORDS
      payload: IPreAfterTaxFileRecord[]
    }
  | { type: OptimizationParameterActionType.SET_CONTRIBUTION; payload: string }
  | {
      type: OptimizationParameterActionType.SET_CONTRIBUTION_AMOUNT
      payload: number
    }
  | { type: OptimizationParameterActionType.TOGGLE_EXPANDED }
  | { type: OptimizationParameterActionType.SET_COMPLETE; payload: boolean }
  | { type: OptimizationParameterActionType.RESET }

// Define the initial state
export const optimizationParameterInitialState: OptimizationParameterState = {
  isExpanded: false,
  isComplete: false,
  optimizationType: OptimizationTypes.Horizon,
  methodType: '',
  objectiveType: '',
  objectiveItem: '',
  riskModel: '',
  benchmark: undefined,
  liabilities: [Liabilities.SpecifyLiabilityToMatch],
  cashFlowFileRecords: [],
  rateFileRecords: [],
  borrowingRateFileRecords: [],
  preAfterTaxFileRecords: {},
  contribution: '',
  amount: 0.0,
}

// Reducer function to handle state transitions
export const optimizationParameterReducer = (
  state: OptimizationParameterState,
  action: Action,
): OptimizationParameterState => {
  switch (action.type) {
    case OptimizationParameterActionType.SET_OPTIMIZATION_TYPE:
      return { ...state, optimizationType: action.payload }
    case OptimizationParameterActionType.SET_METHOD_TYPE:
      return { ...state, methodType: action.payload }
    case OptimizationParameterActionType.SET_OBJECTIVE_TYPE:
      return {
        ...state,
        objectiveType: action.payload,
        cashFlowFileRecords: [],
        rateFileRecords: [],
        borrowingRateFileRecords: [],
        preAfterTaxFileRecords: {},
      }
    case OptimizationParameterActionType.SET_OBJECTIVE_ITEM:
      return { ...state, objectiveItem: action.payload }
    case OptimizationParameterActionType.SET_RISK_MODEL:
      return { ...state, riskModel: action.payload }
    case OptimizationParameterActionType.SET_BENCHMARK:
      return { ...state, benchmark: action.payload }
    case OptimizationParameterActionType.SET_LIABILITIES:
      return { ...state, liabilities: action.payload }
    case OptimizationParameterActionType.SET_CONTRIBUTION:
      return { ...state, contribution: action.payload }
    case OptimizationParameterActionType.SET_CONTRIBUTION_AMOUNT:
      return { ...state, amount: action.payload }
    case OptimizationParameterActionType.SET_CASHFLOW_FILE_RECORDS:
      return { ...state, cashFlowFileRecords: action.payload }
    case OptimizationParameterActionType.SET_REINVESTMENT_RATE_FILE_RECORDS:
      return { ...state, rateFileRecords: action.payload }
    case OptimizationParameterActionType.SET_BORROWING_RATE_FILE_RECORDS:
      return { ...state, borrowingRateFileRecords: action.payload }
    case OptimizationParameterActionType.SET_PRE_AFTER_TAX_FILE_RECORDS:
      return { ...state, preAfterTaxFileRecords: action.payload }
    case OptimizationParameterActionType.TOGGLE_EXPANDED:
      return { ...state, isExpanded: !state.isExpanded }
    case OptimizationParameterActionType.SET_COMPLETE:
      return { ...state, isComplete: action.payload }
    case OptimizationParameterActionType.RESET:
      return { ...optimizationParameterInitialState }
    default:
      throw new Error('Unknown action type')
  }
}
